import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { normalizeRole, ROLE_META } from '@/auth/roles';
import type { HistoryEntry } from '../types';
import { formatDateTime } from '../lib/display';

/** Actor badge color based on actor prefix. */
function actorBadge(actor: string): { label: string; className: string } {
  if (actor.startsWith('system:auto_')) {
    return { label: actor, className: 'bg-green-100 text-green-800 border-green-200' };
  }
  if (actor.startsWith('system:rule_') || actor.startsWith('system:')) {
    return { label: actor, className: 'bg-blue-100 text-blue-800 border-blue-200' };
  }
  if (actor.startsWith('human:')) {
    return { label: actor, className: 'bg-purple-100 text-purple-800 border-purple-200' };
  }
  return { label: actor, className: '' };
}

/** Extract key scores from evidence if present. */
function inlineScores(evidence: Record<string, unknown> | null | undefined): string[] {
  if (!evidence || typeof evidence !== 'object') return [];
  const keys = ['combined_risk', 'frontier_reasoning', 'visual_score', 'text_score'];
  const results: string[] = [];
  for (const k of keys) {
    const val = evidence[k];
    if (val != null && typeof val === 'number') {
      results.push(`${k.replace(/_/g, ' ')}: ${val.toFixed(3)}`);
    }
  }
  return results;
}

interface RichHistoryEntry extends HistoryEntry {
  summary?: string;
  detail?: string;
  actor?: string;
  evidence?: Record<string, unknown> | null;
}

function HistoryRow({ entry }: { entry: RichHistoryEntry }) {
  const [expanded, setExpanded] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const role = normalizeRole(entry.moderatorRole);
  const actor = entry.actor || entry.moderator;
  const badge = actorBadge(actor);
  const scores = inlineScores(entry.evidence);

  return (
    <li className="relative border-l pl-4">
      <span className="absolute -left-[5px] top-1.5 size-2.5 rounded-full bg-primary" />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{entry.action}</span>
        {role && <Badge variant="outline">{ROLE_META[role].label}</Badge>}
        <Badge variant="outline" className={cn('text-[10px]', badge.className)}>
          {badge.label}
        </Badge>
        <span className="text-xs text-muted-foreground">{formatDateTime(entry.date)}</span>
      </div>

      {/* Summary — shown prominently */}
      {entry.summary && (
        <p className="mt-1 text-sm font-medium text-foreground">{entry.summary}</p>
      )}

      {/* Fallback to description if no summary */}
      {!entry.summary && entry.description && (
        <p className="mt-1 text-sm text-muted-foreground">{entry.description}</p>
      )}

      {/* Inline scores from evidence */}
      {scores.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-2">
          {scores.map((s) => (
            <span key={s} className="rounded bg-muted px-1.5 py-0.5 text-[10px] tabular-nums">
              {s}
            </span>
          ))}
        </div>
      )}

      {/* Expandable detail */}
      {entry.detail && (
        <button
          type="button"
          className="mt-1 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setExpanded((v) => !v)}
        >
          <ChevronDown className={cn('size-3 transition-transform', expanded ? '' : '-rotate-90')} />
          Detail
        </button>
      )}
      {expanded && entry.detail && (
        <p className="mt-1 rounded border bg-muted/50 p-2 text-xs text-muted-foreground whitespace-pre-wrap">
          {entry.detail}
        </p>
      )}

      {/* Expandable evidence */}
      {entry.evidence && Object.keys(entry.evidence).length > 0 && (
        <>
          <button
            type="button"
            className="mt-1 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setEvidenceOpen((v) => !v)}
          >
            <ChevronDown className={cn('size-3 transition-transform', evidenceOpen ? '' : '-rotate-90')} />
            Evidence
          </button>
          {evidenceOpen && (
            <pre className="mt-1 max-h-40 overflow-auto rounded border bg-muted/50 p-2 text-[10px]">
              {JSON.stringify(entry.evidence, null, 2)}
            </pre>
          )}
        </>
      )}
    </li>
  );
}

/**
 * Merged history timeline (doc 09 §4.10). Shows summary prominently, actor badges,
 * expandable detail + evidence sections, and inline scores.
 */
export function ModerationHistoryCard({ history }: { history: RichHistoryEntry[] }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm">Moderation History</CardTitle>
        <span className="text-xs text-muted-foreground">Total Actions: {history.length}</span>
      </CardHeader>
      <CardContent>
        {history.length === 0 ? (
          <p className="text-sm text-muted-foreground">No moderation history yet.</p>
        ) : (
          <ol className="max-h-80 space-y-4 overflow-y-auto pr-2">
            {history.map((h, i) => (
              <HistoryRow key={`${h.action}-${h.date}-${i}`} entry={h} />
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
